/*
 * The admin router, built from the manifest exactly as the recruiter app's is.
 *
 * One thing differs and it is the reason this app exists separately: the `refused` session state.
 * An account can authenticate perfectly and still not be an operator, and that is not "signed
 * out" — sending them back through sign-in would loop. It gets its own screen saying plainly what
 * happened.
 */

import { Suspense, lazy, useEffect, useMemo } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  Link,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { AppShell, Button, Card, FullBleed, Nav, Spinner, Stack } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';
import { ROUTES } from './routes.js';
import type { RouteManifestEntry } from './routes.js';
import { useSession } from './auth/session.js';

function RootLayout() {
  const { t } = useI18n();
  const { status, operator, signOut } = useSession();
  // The *matched route's* pattern, not the address bar. `/tenants/abc-123` is never equal to
  // `/tenants/$tenantId`, so comparing pathnames finds no entry for any parameterised route and
  // the layout below quietly falls through to a bare `<Outlet />` — the tenant detail screen
  // rendered with no header, no navigation and no way to sign out, and nothing threw.
  // `String(...)` is load-bearing. `matches` is `RouteMatch<any, ...>`, so `routeId` is `any`,
  // and `useRouterState` resolves its result as `unknown extends TSelected ? RouterState : …` —
  // which `any` satisfies, handing back the entire router state instead of the selection.
  const routeId = useRouterState({
    select: (state) => String(state.matches.at(-1)?.routeId ?? ''),
  });

  const entry = ROUTES.find((candidate) => candidate.path === routeId);

  if (status === 'loading') {
    return (
      <FullBleed panelExempt="The session is still resolving; there is nothing to assist with yet.">
        <Card title={t('common.loading')}>
          <Spinner />
        </Card>
      </FullBleed>
    );
  }

  if (status === 'refused') {
    // Authenticated, and not an operator. Sign-out is offered rather than a retry, because
    // retrying produces exactly the same answer.
    return (
      <FullBleed panelExempt="No operator session exists, so there is no context for a copilot.">
        <Card title={t('admin.refused.title')} description={t('admin.refused.description')}>
          <Button variant="primary" onClick={signOut}>
            {t('auth.signOut.action')}
          </Button>
        </Card>
      </FullBleed>
    );
  }

  if (!entry?.authenticated || status !== 'authenticated') {
    return <Outlet />;
  }

  const navItems = ROUTES.filter(
    (candidate) => candidate.nav && (!candidate.superAdminOnly || operator?.canProvision),
  ).sort((a, b) => (a.nav?.order ?? 0) - (b.nav?.order ?? 0));

  return (
    <AppShell
      labels={{ nav: t('admin.nav.tenants'), copilot: t('admin.name') }}
      brand={t('admin.name')}
      header={
        <>
          <span>{operator?.email}</span>
          <Stack direction="row" gap={3} align="center">
            <span className="tf-tag">{operator?.roleKey}</span>
            <Button size="sm" variant="ghost" onClick={signOut}>
              {t('auth.signOut.action')}
            </Button>
          </Stack>
        </>
      }
      /*
       * The same component the recruiter app uses, with one headingless group — M-25.
       *
       * **No sections here, deliberately.** This console has three items and they are three steps
       * of one job; headings over them would be three labels for one list. `Nav` takes
       * `eyebrow: null` for exactly this, and `gates/route-manifest-lint.mjs` holds the app to it
       * — a nav entry declaring a section fails there, so "unsectioned" is a checked property
       * rather than a thing nobody got round to.
       */
      nav={
        <Nav
          label={t('admin.nav.tenants')}
          groups={[
            {
              key: 'console',
              eyebrow: null,
              items: navItems.map((item) => ({
                key: item.id,
                label: t(item.nav!.labelKey),
                href: item.path,
              })),
            },
          ]}
          /*
            The console does not dock — three items never cost enough width to be worth taking
            back — but it takes `content` all the same, so the link's insides are described in one
            place. Rendering `item.label` here would be a second answer to the same question.
          */
          renderLink={(item, className, content) => (
            <Link key={item.key} to={item.href} className={className}>
              {content}
            </Link>
          )}
        />
      }
      // No copilot on the administration console, and the region is still reserved by AppShell
      // (FE-04). D-07's persistent panel is a recruiter feature; an operator provisioning a
      // tenant has nothing for it to assist with yet.
      copilot={null}
    >
      <Outlet />
    </AppShell>
  );
}

function Guard({ entry, children }: { entry: RouteManifestEntry; children: ReactNode }) {
  const { t } = useI18n();
  const { status, operator } = useSession();
  const navigate = useNavigate();

  const mustSignIn = entry.authenticated && status === 'anonymous';

  // In an effect, not in the render body. Navigating during render updates the router while
  // React is rendering this component, which React reports as "Cannot update a component while
  // rendering a different component" in every operator's console.
  useEffect(() => {
    if (!mustSignIn) {
      return;
    }
    const returnTo = `${window.location.pathname}${window.location.search}`;
    void navigate({ to: '/sign-in', search: { returnTo }, replace: true });
  }, [mustSignIn, navigate]);

  if (mustSignIn) {
    return null;
  }

  if (status === 'loading') {
    return <Spinner />;
  }

  // The server refuses this regardless — createPlatformPolicyDecider permits `create` to
  // platform_super_admin only. This is so a support operator is not offered a form that will
  // fail on submit.
  if (entry.superAdminOnly && !operator?.canProvision) {
    return (
      <Card title={t('error.unauthorised.title')}>{t('admin.error.forbidden')}</Card>
    );
  }

  return <>{children}</>;
}

function NotFound() {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <FullBleed panelExempt="No screen is registered here, so there is no context for a copilot.">
      <Card title={t('error.notFound.title')} description={t('error.notFound.description')}>
        <Button variant="primary" onClick={() => void navigate({ to: '/' })}>
          {t('admin.nav.tenants')}
        </Button>
      </Card>
    </FullBleed>
  );
}

const rootRoute = createRootRoute({ component: RootLayout, notFoundComponent: NotFound });

function buildRouter() {
  const children = ROUTES.map((entry) => {
    const Screen = lazy(entry.load as () => Promise<{ default: ComponentType }>);

    return createRoute({
      getParentRoute: () => rootRoute,
      path: entry.path,
      component: () => (
        <Guard entry={entry}>
          <Suspense fallback={<Spinner />}>
            <Screen />
          </Suspense>
        </Guard>
      ),
    });
  });

  return createRouter({
    routeTree: rootRoute.addChildren(children),
    defaultNotFoundComponent: NotFound,
  });
}

export function App() {
  const router = useMemo(buildRouter, []);
  return <RouterProvider router={router} />;
}
